import { useState } from 'react'
import { FiCheck, FiX, FiAlertCircle, FiUsers } from 'react-icons/fi'
import toast from 'react-hot-toast'
import { Btn } from '../../ui'
import { useValidacionesPendientes } from '../../hooks/useValidacionesPendientes'
import { formatearFecha, formatearHora } from '../../utils/fechas'

/*
 * ModalValidacionSemanalV2 — DS v2.
 * Modal que aparece automáticamente cuando hay validaciones pendientes.
 * El empleado revisa los marcajes que su manager hizo en grupal y los
 * valida o rechaza individualmente, o de golpe.
 */
const ModalValidacionSemanalV2 = ({ onClose }) => {
  const { pendientes, validar, rechazar, validarTodas } = useValidacionesPendientes()
  const [enviando, setEnviando] = useState(false)
  const [rechazando, setRechazando] = useState(null) // {id} o null

  const handleValidar = async (id) => {
    setEnviando(true)
    try { await validar(id); toast.success('Marcaje validado') }
    catch (e) { toast.error(e.message || 'Error') }
    finally { setEnviando(false) }
  }

  const handleValidarTodas = async () => {
    setEnviando(true)
    try {
      const r = await validarTodas()
      toast.success(`${r.validadas} marcajes validados`)
      onClose?.()
    } catch (e) { toast.error(e.message || 'Error') }
    finally { setEnviando(false) }
  }

  if (pendientes.length === 0) return null

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
      <div className="ui-card w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-start justify-between p-5 border-b border-[var(--line)]">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-soft bg-[rgba(124,58,237,.10)] flex items-center justify-center">
              <FiUsers size={15} className="text-[#7C3AED]" />
            </div>
            <div>
              <div className="eyebrow mb-1">Validación semanal</div>
              <h3 className="font-display text-[20px] text-[var(--ink)] leading-tight">
                {pendientes.length} marcaje{pendientes.length === 1 ? '' : 's'} pendiente{pendientes.length === 1 ? '' : 's'} de validar.
              </h3>
              <p className="text-[12px] text-[var(--ink-3)] mt-1">
                Tu manager registró estos marcajes en grupal. Confirma que son correctos.
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-soft text-[var(--ink-3)] hover:text-[var(--ink)] hover:bg-[var(--bg-2)]" disabled={enviando}>
            <FiX size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {pendientes.map((v) => (
            <div key={v.id} className="rounded-soft border border-[var(--line)] p-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-[13px] font-medium text-[var(--ink)]">
                    {formatearFecha(v.fecha)} · {v.componente === 'entrada' ? 'Entrada' : 'Salida'} {v.componente === 'entrada' ? formatearHora(v.hora_entrada) : formatearHora(v.hora_salida)}
                  </div>
                  <div className="text-[11px] text-[var(--ink-3)] mt-0.5">
                    Registrado por {v.componente === 'entrada' ? v.creado_por_entrada : v.creado_por_salida}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setRechazando(v.id)}
                    className="px-3 py-1.5 text-[12px] rounded-soft text-[#C13030] hover:bg-[rgba(193,48,48,.08)]"
                    disabled={enviando}
                  >
                    Rechazar
                  </button>
                  <button
                    onClick={() => handleValidar(v.id)}
                    className="px-3 py-1.5 text-[12px] rounded-soft bg-[var(--brand-1)] text-white hover:opacity-90 flex items-center gap-1"
                    disabled={enviando}
                  >
                    <FiCheck size={12} /> Validar
                  </button>
                </div>
              </div>
              {rechazando === v.id && (
                <FormularioRechazo
                  v={v}
                  onCancel={() => setRechazando(null)}
                  onConfirm={async (obs, hora) => {
                    setEnviando(true)
                    try {
                      await rechazar(v.id, obs, hora)
                      toast.success('Marcaje rechazado, solicitud creada')
                      setRechazando(null)
                    } catch (e) { toast.error(e.message || 'Error') }
                    finally { setEnviando(false) }
                  }}
                />
              )}
            </div>
          ))}
        </div>

        <div className="flex gap-3 p-5 border-t border-[var(--line)]">
          <Btn type="button" variant="ghost" className="flex-1" onClick={onClose} disabled={enviando}>
            Más tarde
          </Btn>
          <Btn type="button" variant="primary" className="flex-1" onClick={handleValidarTodas} disabled={enviando}>
            {enviando ? 'Validando…' : 'Validar todos'}
          </Btn>
        </div>
      </div>
    </div>
  )
}

const FormularioRechazo = ({ v, onCancel, onConfirm }) => {
  const [obs, setObs] = useState('')
  const [hora, setHora] = useState(v.componente === 'entrada' ? v.hora_entrada : v.hora_salida)
  return (
    <div className="mt-3 pt-3 border-t border-[var(--line)] space-y-2">
      <div className="flex items-center gap-2 text-[11px] text-[#A86A06]">
        <FiAlertCircle size={12} /> Indica la hora correcta y un comentario.
      </div>
      <input
        type="time"
        value={hora || ''}
        onChange={(e) => setHora(e.target.value)}
        className="w-full px-2 py-1.5 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[12px]"
      />
      <textarea
        rows={2}
        value={obs}
        onChange={(e) => setObs(e.target.value)}
        placeholder="Comentario (opcional)"
        className="w-full px-2 py-1.5 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[12px] resize-none"
      />
      <div className="flex gap-2">
        <button onClick={onCancel} className="flex-1 px-3 py-1.5 text-[12px] rounded-soft bg-[var(--bg-2)] hover:bg-[var(--line)]">
          Cancelar
        </button>
        <button onClick={() => onConfirm(obs, hora)} className="flex-1 px-3 py-1.5 text-[12px] rounded-soft bg-[#C13030] text-white hover:opacity-90">
          Confirmar rechazo
        </button>
      </div>
    </div>
  )
}

export default ModalValidacionSemanalV2
